import { CustomerApi } from "@/api";
import { getDefaultApiConfig } from "@/utils";

export interface CustomerDetail {
  id: number;
  firstName: string;
  lastName: string;
  age: number | null;
}

const cache = new Map<number, CustomerDetail>();
const inFlight = new Map<number, Promise<CustomerDetail | null>>();

let api: CustomerApi | null = null;

function getApi(): CustomerApi {
  if (!api) {
    api = new CustomerApi(getDefaultApiConfig());
  }
  return api;
}

export function computeAge(
  dateOfBirth: string | null | undefined
): number | null {
  if (!dateOfBirth) {
    return null;
  }
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

export function getCachedCustomerDetail(
  customerId: number
): CustomerDetail | null {
  return cache.get(customerId) ?? null;
}

export function fetchCustomerDetail(
  customerId: number
): Promise<CustomerDetail | null> {
  const cached = cache.get(customerId);
  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = inFlight.get(customerId);
  if (pending) {
    return pending;
  }

  const promise = getApi()
    .getCustomer(customerId)
    .then((response) => {
      const data = response.data;
      const detail: CustomerDetail = {
        id: data.id ?? customerId,
        firstName: data.firstName,
        lastName: data.lastName,
        age: computeAge(data.dateOfBirth),
      };
      cache.set(customerId, detail);
      return detail;
    })
    .catch(() => null)
    .finally(() => {
      inFlight.delete(customerId);
    });

  inFlight.set(customerId, promise);
  return promise;
}
